import { Media } from "./Media";
import { Post } from "./Post";
import { VideoScript } from "./VideoScript";
import { Coach, Player, User } from "./UserAccount";
import { Pagination as BasePagination } from "./Page";

export namespace VideoProject {
  export interface Pagination extends BasePagination {
    totalPriorityCount?: number;
  }

  export namespace Preferences {
    export enum Tone {
      FUN = "FUN",
      PROFESSIONAL = "PROFESSIONAL",
      SERIOUS = "SERIOUS",
    }

    export enum MusicGenre {
      JAZZ = "JAZZ",
      CHILL = "CHILL",
      ROCK = "ROCK",
      LOFI = "LOFI",
      LOUNGE = "LOUNGE",
      HIPHOP = "HIPHOP",
      LATIN = "LATIN",
    }
  }

  export interface Preferences {
    id: number;
    videoProjectId: number;
    captions: boolean;
    emojis: boolean;
    bRoll: boolean;
    music: boolean;
    captionColors?: string | null;
    tone?: Preferences.Tone | null;
    musicGenre?: Preferences.MusicGenre | null;
  }

  export interface ClientNote {
    id?: number;
    playerId?: number | null;
    coachId?: number | null;
    notes: string;
    createdDate?: string;
    updatedDate?: string;
  }

  export type ExtraFields = {
    preferences?: Preferences;
    player?: Player;
    coach?: Coach;
    originalProject?: VideoProject;
    post?: Post;
    videoScript?: VideoScript;
    originalMedia?: Media[];
    editedMedia?: Media[];
    needsAck?: boolean;
    isPriority?: boolean;
    queueAssignments?: QueueAssignment[];
    messages?: Message[];
    clientNotes?: ClientNote;
    coachClientNotes?: ClientNote;
    qaAudits?: QaAudit[];
  };

  export interface Message {
    id: number;
    userId: number;
    userName: string | null;
    userRole: "PLAYER" | "COACH" | "EDITOR" | null;
    content: string | null;
    createdDate: string;
  }

  export interface QueueAssignment {
    id: number;
    coachId: number;
    staffUserId: number;
    role: "EDITOR" | "QA";
    staffUser?: User | null;
    createdDate?: string;
    updatedDate?: string;
  }

  export enum Status {
    SUBMITTED_FOR_EDITING = "SUBMITTED_FOR_EDITING",
    EDITING = "EDITING",
    SUBMITTED_FOR_QA_REVIEW = "SUBMITTED_FOR_QA_REVIEW",
    QA_REVIEW = "QA_REVIEW",
    READY_TO_POST = "READY_TO_POST",
    SCHEDULED = "SCHEDULED",
    POSTED = "POSTED",
    POST_FAILED = "POST_FAILED",
  }

  export const StatusMeta: Record<`${Status}`, { label: string }> = {
    [Status.SUBMITTED_FOR_EDITING]: { label: "Submitted for Editing" },
    [Status.EDITING]: { label: "Editing" },
    [Status.SUBMITTED_FOR_QA_REVIEW]: { label: "Submitted for QA Review" },
    [Status.QA_REVIEW]: { label: "QA Review" },
    [Status.READY_TO_POST]: { label: "Ready to Post" },
    [Status.SCHEDULED]: { label: "Scheduled" },
    [Status.POSTED]: { label: "Posted" },
    [Status.POST_FAILED]: { label: "Post Failed" },
  };

  export const InternalStatuses = [Status.EDITING, Status.SUBMITTED_FOR_QA_REVIEW, Status.QA_REVIEW] as const;
  export const CompletedStatuses = [Status.READY_TO_POST, Status.SCHEDULED] as const;

  export const FilterableStatuses = Object.values(Status).filter(
    status => !(InternalStatuses as readonly Status[]).includes(status)
  );

  export function isInternalStatus(status?: Status | string | null): boolean {
    return status != null && (InternalStatuses as readonly string[]).includes(status);
  }

  export function isCompletedStatus(status?: Status | string | null): boolean {
    return status != null && (CompletedStatuses as readonly string[]).includes(status);
  }

  export function clientStatus<T extends Status | string>(status: T): T | Status.SUBMITTED_FOR_EDITING {
    return isInternalStatus(status) ? Status.SUBMITTED_FOR_EDITING : status;
  }

  export function statusesForFilter(statuses: Status[]): Status[] {
    if (statuses.includes(Status.SUBMITTED_FOR_EDITING)) {
      return [...new Set<Status>([...statuses, ...InternalStatuses])];
    }
    return statuses;
  }

  export enum Event {
    START_EDITING = "start_editing",
    STOP_EDITING = "stop_editing",
    SUBMIT_FOR_QA_REVIEW = "submit_for_qa_review",
    COMPLETE = "complete",
    RESUBMIT = "resubmit",
    RETURN_TO_EDITING = "return_to_editing",
    START_QA_REVIEW = "start_qa_review",
    STOP_QA_REVIEW = "stop_qa_review",
    REJECT_QA = "reject_qa",
    APPROVE_QA = "approve_qa",
    REQUEST_REVISION = "request_revision",
    ADD_MESSAGE = "add_message",
    REOPEN_EDITING = "reopen_editing",
    SCHEDULE = "schedule",
    FAIL_POST = "fail_post",
    SUCCEED_POST = "succeed_post",
    ASSIGN = "assign",
    UPLOAD_MEDIA = "upload_media",
    SAVE_DRAFT = "save_draft",
    ARCHIVE = "archive",
    DUPLICATE = "duplicate",
  }

  export const QaIssueCategories = [
    "ON_SCREEN_AND_POST_TEXT",
    "STYLE_PREFERENCES",
    "REQUESTS_AND_NOTES",
    "AUDIO_VISUAL",
    "OTHER",
  ] as const;
  export type QaIssueCategory = (typeof QaIssueCategories)[number];

  export const QaIssueCategoryMeta: Record<
    QaIssueCategory,
    { label: string; description: string; lookFor: string[]; shortcut: string }
  > = {
    ON_SCREEN_AND_POST_TEXT: {
      label: "On-screen & post text",
      description:
        "Wrong words in titles, lower-thirds, burned-in captions, or the social post. (Use Style preferences if captions are off or the color is wrong.)",
      lookFor: [
        "Misspelling",
        "Wrong or missing word",
        "Leftover instruction text",
        "Duplicate or extra lines in the post",
        "On-screen text doesn't match speech",
      ],
      shortcut: "1",
    },
    STYLE_PREFERENCES: {
      label: "Style preferences",
      description: "Doesn't match the Player's saved style preferences.",
      lookFor: [
        "Captions, emojis, video clips, or music on/off",
        "Wrong music genre",
        "Wrong tone",
        "Wrong caption colors",
      ],
      shortcut: "2",
    },
    REQUESTS_AND_NOTES: {
      label: "Requests & notes",
      description:
        "A project special request, in-thread instruction, or standing Player/Coach note was skipped or done wrong.",
      lookFor: [
        "Special request not followed",
        "Player or Coach note not followed",
        "Wrong/missing logo, end slate, disclosure, or contact info",
        "Specified first frame, wording, or hook not used",
        "Project request overrides a conflicting note",
      ],
      shortcut: "3",
    },
    AUDIO_VISUAL: {
      label: "Audio/Visual",
      description: "Any audio or visual edit mistake.",
      lookFor: [
        "Cutoff or abrupt ending",
        "Pause/fumble left in",
        "Wrong clip order",
        "Mirrored video",
        "Green spill or edit artifacts",
        "Wrong thumbnail or first frame",
        "Bad crop",
        "Music too loud, stacked, or missing",
        "Audio worse than the raw",
      ],
      shortcut: "4",
    },
    OTHER: {
      label: "Other",
      description: "Editor miss that doesn't fall under one of the categories above.",
      lookFor: ["Wrong file used", "Graphic or listing mismatch", "Anything else wrong that isn't a new client ask"],
      shortcut: "5",
    },
  };

  export function qaIssueCategoryLabel(category: QaIssueCategory) {
    return QaIssueCategoryMeta[category].label;
  }

  export interface QaIssue {
    category: QaIssueCategory;
    comment?: string | null;
    timestampMs?: number | null;
  }

  export interface QaAuditIssue {
    id: number;
    category: QaIssueCategory;
    comment?: string | null;
    timestampMs?: number | null;
    createdDate?: string;
  }

  export interface QaAudit {
    id: number;
    qaUserId: number;
    editorUserId?: number | null;
    result: "APPROVED" | "REJECTED";
    createdDate?: string;
    qaAuditIssues?: QaAuditIssue[];
    editedMedia?: Media[];
  }

  export interface EventRequest {
    event: Event | `${Event}`;
    role?: QueueAssignment["role"];
    fromUserId?: number | null;
    toUserId?: number | null;
    editorUserId?: number | null;
    qaUserId?: number | null;
    originalMedia?: Media[];
    editedMedia?: Media[];
    issues?: QaIssue[];
    message?: string;
    hashtags?: string;
    isAutoPost?: boolean;
    preferredPostDate?: string;
    allowShortLink?: boolean;
    allowCompliance?: boolean;
    allowPlayerHashtags?: boolean;
    socialNetworkIds?: string[];
    shouldArchiveCurrentProject?: boolean;
    editedSubtitle?: { uri: string; content?: string };
  }

  export enum Source {
    PLAYER_WEB = "PLAYER_WEB",
    COACH_WEB = "COACH_WEB",
    PLAYER_MOBILE = "PLAYER_MOBILE",
  }

  export enum AckType {
    MESSAGES = "MESSAGES",
  }
}

export interface VideoProject extends VideoProject.ExtraFields {
  id: number;
  playerId: number;
  coachId: number;
  originalProjectId?: number | null;
  postId?: number | null;
  videoScriptId?: number | null;
  status: VideoProject.Status;
  source?: VideoProject.Source | null;
  statusLabel?: string;
  statusDate?: string | null;
  preferredPostDate?: string | null;
  isTestProject?: boolean;
  editorUserId?: number | null;
  editedDate?: string | null;
  autoCaption?: boolean;
  isAutoPost?: boolean;
  allowPlayerHashtags?: boolean;
  allowShortLink?: boolean;
  allowCompliance?: boolean;
  createdDate?: string;
  updatedDate?: string;
  requiresQa?: boolean;
  qaUserId?: number | null;
  qaStatus?: "APPROVED" | "REJECTED" | null;
  qaDate?: string | null;
  revisionCount?: number;
  qaFailureCount?: number;
  archived?: boolean;
  playerName?: string | null;
  playerPhotoUrl?: string | null;
  companyName?: string | null;
  thumbnailUrl?: string | null;
}

export interface VideoCatalystUsageReport {
  coachId: number;
  startDate: string;
  endDate: string;
  playerUsage: VideoCatalystPlayerUsage[];
}

export interface VideoCatalystPlayerUsage {
  playerId: number;
  playerName: string;
  playerEmail: string;
  currentlyEnabled: boolean;
  currentCreditLimit: number | null;
  creditsUsed: number;
  regionNames: string[];
  branchNames: string[];
  costCenterNames: string[];
  complianceDisclaimer: string | null;
  internalIdentifier: string | null;
}

export interface VideoProjectQueueSearchParams {
  searchTerm?: string | null;
  includeDemoProjects?: boolean;
  playerId?: number;
  status?: VideoProject.Status[];
  assignees?: (number | null)[];
  priority?: boolean;
  queue?: "mine" | "general" | "all";
  page?: number;
  perPage?: number;
  sort?: string;
  order?: "asc" | "desc";
  createdAfter?: string;
  createdBefore?: string;
  cursor?: string;
  includeTotal?: boolean;
}

export interface VideoProjectQueueAssignmentsPayload {
  coachId: number;
  requireQaOnVideos: boolean;
  assignments: VideoProject.QueueAssignment[];
}

export interface VideoProjectQueueAssignmentsUpdateRequest {
  assignments: Array<{ staffUserId: number; role: VideoProject.QueueAssignment["role"] }>;
  requireQaOnVideos?: boolean;
}

export const EDITOR_VIDEO_PROJECT_EXTRA_FIELDS = {
  messages: true,
  player: ["industry_roles", "aliases", "web_address", "region_names", "branch_names"],
  preferences: true,
  post: true,
  videoScript: true,
  originalMedia: true,
  editedMedia: true,
  clientNotes: true,
  coachClientNotes: true,
  needsAck: true,
  qaAudits: true,
} as const;
